import { useState } from "react";
import { motion } from "framer-motion";
import { submitLessonExitQuiz } from "@/lib/api";
import { MathRichText } from "@/lib/mathRichText";
import { usePlayerAuth } from "@/context/PlayerAuthContext";
import type { ExitQuizApiResponse, ExitQuizSubmitResult } from "@/types/api";

interface PostLessonQuizProps {
  quiz: ExitQuizApiResponse;
  courseId: number;
  lessonId: number;
  isChildTheme: boolean;
  /** Admin preview — don't POST to submit endpoint */
  isAdminPreview?: boolean;
  onContinue: () => void;
}

export function PostLessonQuiz({
  quiz,
  courseId,
  lessonId,
  isChildTheme,
  isAdminPreview,
  onContinue,
}: PostLessonQuizProps) {
  const { loginUrl } = usePlayerAuth();
  // maps questionId → selected option's server ID (not index)
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsLogin, setNeedsLogin] = useState(false);
  const [result, setResult] = useState<ExitQuizSubmitResult | null>(null);

  const questions = quiz.questions;
  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id] !== undefined);

  const handleSubmit = async () => {
    if (!allAnswered) return;
    setSubmitting(true);
    setError(null);
    setNeedsLogin(false);
    try {
      if (isAdminPreview) {
        setResult({
          success: true,
          message: "Admin preview — your answers weren't saved to the server.",
        });
        setSubmitting(false);
        return;
      }
      // New submission format: array of { question_id, option_id }
      const answersArray = questions.map((q) => ({
        question_id: q.id,
        option_id: answers[q.id]!,
      }));
      const res = await submitLessonExitQuiz(courseId, lessonId, {
        quiz_id: quiz.quiz_id,
        answers: answersArray,
      });
      if (res.success === false) {
        setNeedsLogin(res.requiresLogin === true);
        setError(res.message?.trim() || "Could not save your quiz.");
        setSubmitting(false);
        return;
      }
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  if (result && result.success !== false) {
    const pct = result.percent ?? result.score_percent;
    // New endpoint returns `correct` and `total`; legacy returns `score` and `total`
    const correct = result.correct ?? result.score;
    const total = result.total ?? result.max_score;
    const scoreLine =
      correct != null && total != null
        ? `${correct} / ${total}`
        : pct != null
          ? `${pct}%`
          : null;
    const passed = result.passed ?? (correct != null && total != null && correct === total);

    return (
      <motion.div
        className="flex flex-col flex-1 min-h-0 max-w-xl w-full mx-auto px-4 py-6"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="text-center mb-6">
          <p className="text-4xl mb-2" aria-hidden>
            {passed ? "🎉" : "✅"}
          </p>
          <h2
            className="text-xl font-bold text-slate-800 mb-2"
            style={{ fontFamily: "var(--lp-font-heading)" }}
          >
            Quiz results
          </h2>
          {scoreLine && (
            <p className="text-2xl font-semibold text-indigo-700">
              {scoreLine}
              {pct != null && correct != null && total != null && (
                <span className="text-lg font-normal text-slate-600 ml-2">({pct}%)</span>
              )}
            </p>
          )}
          {result.message && <p className="text-sm text-slate-600 mt-2">{result.message}</p>}
          {!scoreLine && !result.results?.length && !result.message?.trim() && (
            <p className="text-sm text-slate-600 mt-2">Your quiz attempt was saved.</p>
          )}
        </div>

        {result.results && result.results.length > 0 && (
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1 text-left">
            {result.results.map((row, ri) => (
              <div
                key={`${row.question_id}-${ri}`}
                className={`rounded-xl border p-4 shadow-sm ${
                  row.is_correct ? "border-emerald-200 bg-emerald-50/80" : "border-amber-200 bg-amber-50/80"
                }`}
              >
                <div className="text-sm font-semibold text-slate-800 mb-2">
                  {ri + 1}. <MathRichText text={row.question} />
                </div>
                <ul className="space-y-1.5">
                  {row.options.map((opt, oi) => {
                    const isUserPick = oi === row.submitted_idx;
                    const isCorrect = opt.is_correct;
                    return (
                      <li
                        key={oi}
                        className={`rounded-lg px-3 py-2 text-sm flex items-start gap-2 border ${
                          isCorrect
                            ? "border-emerald-400 bg-white/90 font-medium text-emerald-900"
                            : isUserPick && !isCorrect
                              ? "border-red-300 bg-red-50 text-red-900"
                              : "border-transparent text-slate-600"
                        }`}
                      >
                        <span className="shrink-0 mt-0.5" aria-hidden>
                          {isCorrect ? "✓" : isUserPick ? "✗" : "·"}
                        </span>
                        <span>
                          <MathRichText text={opt.text} />
                        </span>
                        {isCorrect && (
                          <span className="ml-auto text-xs font-semibold text-emerald-700 shrink-0">
                            Correct
                          </span>
                        )}
                        {isUserPick && !isCorrect && (
                          <span className="ml-auto text-xs font-medium text-red-700 shrink-0">
                            Your answer
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}

        <div className="pt-6 flex justify-center">
          <motion.button
            type="button"
            onClick={onContinue}
            className="px-8 py-3 rounded-full font-semibold text-white shadow-lg"
            style={{
              background: "linear-gradient(90deg, var(--lp-header-from), var(--lp-header-to))",
            }}
            whileHover={isChildTheme ? { scale: 1.03 } : undefined}
            whileTap={{ scale: 0.98 }}
          >
            Continue
          </motion.button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="flex flex-col flex-1 min-h-0 max-w-xl w-full mx-auto px-4 py-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <h2
        className="text-lg sm:text-xl font-bold text-slate-800 mb-1 text-center"
        style={{ fontFamily: "var(--lp-font-heading)" }}
      >
        <MathRichText text={quiz.title || "Quick check"} />
      </h2>
      <p className="text-xs text-slate-500 text-center mb-6">
        {isAdminPreview
          ? "Admin preview — submit shows this screen only; nothing is recorded."
          : "Answer all questions, then submit. Your attempt is saved to your account."}
      </p>

      <div className="flex-1 min-h-0 overflow-y-auto space-y-6 pr-1">
        {questions.map((q, qi) => (
          <fieldset
            key={q.id}
            className="rounded-xl border border-slate-200 bg-white/90 p-4 shadow-sm text-left"
          >
            <legend className="text-sm font-semibold text-slate-800 px-1">
              <span>
                {qi + 1}. <MathRichText text={q.question} />
              </span>
            </legend>
            <div className="mt-3 space-y-2">
              {q.options.map((opt) => (
                <label
                  key={opt.id}
                  className={`flex items-start gap-2 cursor-pointer rounded-lg border p-2 text-sm transition-colors ${
                    answers[q.id] === opt.id
                      ? "border-indigo-400 bg-indigo-50"
                      : "border-transparent hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="radio"
                    className="mt-0.5 shrink-0"
                    name={`exit-q-${q.id}`}
                    checked={answers[q.id] === opt.id}
                    onChange={() => setAnswers((prev) => ({ ...prev, [q.id]: opt.id }))}
                  />
                  <span className="text-slate-700">
                    <MathRichText text={opt.text} />
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        ))}
      </div>

      {error && (
        <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-4 space-y-2">
          <p>{error}</p>
          {needsLogin && (
            <a
              href={loginUrl}
              className="inline-flex font-semibold text-indigo-700 hover:text-indigo-900 underline"
            >
              Log in to save your score
            </a>
          )}
        </div>
      )}

      <div className="pt-6 flex justify-center">
        <motion.button
          type="button"
          disabled={!allAnswered || submitting}
          onClick={() => void handleSubmit()}
          className="px-8 py-3 rounded-full font-semibold text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: "linear-gradient(90deg, var(--lp-header-from), var(--lp-header-to))",
          }}
          whileHover={isChildTheme && allAnswered ? { scale: 1.03 } : undefined}
          whileTap={{ scale: 0.98 }}
        >
          {submitting ? "Submitting…" : "Submit answers"}
        </motion.button>
      </div>
    </motion.div>
  );
}
